import React from 'react';
import classNames from 'classnames';

// Utils
import type { StrokePoint } from '../utils/drawingSvgPathUtils';
import { getSvgPathFromStroke } from '../utils/drawingSvgPathUtils';
import { getPathStroke, getStrokeColor, type FreehandPoint } from '../drawingEditorUtils';
import DrawingPathSvg from './DrawingPathSvg';

type DrawingPathProps = {
    id?: string;
    className?: string;
    stroke?: StrokePoint[];
    points: FreehandPoint[];
    size: number;
    hasPressure?: boolean;
    thinning?: number;
    smoothing?: number;
    streamline?: number;
    color?: string;
    svgPath?: string;
    isStrokeOnly?: boolean;
    isSelected?: boolean;
    debugUpdateAllStrokes?: boolean;
    debugDrawingLastUpdateTime?: number;
    pathOnClick?: () => void;
} & Omit<React.SVGProps<SVGPathElement>, 'color' | 'points' | 'stroke'>;

const DrawingPath: React.FC<DrawingPathProps> = (props) => {
    const {
        className,
        stroke,
        points,
        size,
        color,
        svgPath,
        isStrokeOnly,
        thinning,
        smoothing,
        streamline,
        hasPressure,
        debugUpdateAllStrokes,

        debugDrawingLastUpdateTime,
        ...rest
    } = props;

    const drawingPathClassName = classNames('DrawingPath', className);

    if (svgPath && !debugUpdateAllStrokes) {
        return (
            <DrawingPathSvg
                className={drawingPathClassName}
                color={color}
                getStrokeColor={getStrokeColor}
                isStrokeOnly={isStrokeOnly}
                size={size}
                svgPath={svgPath}
                {...rest}
            />
        );
    }

    // Fallback to rendering from points/stroke while drawing before svgPath exists, or
    // while debugging where we intentionally recompute stroke parameters in real time.
    const renderedStroke = debugUpdateAllStrokes
        ? getPathStroke({ points, size, thinning, smoothing, streamline, hasPressure })
        : stroke || getPathStroke({ points, size, thinning, smoothing, streamline, hasPressure });
    const isDerivedStrokeOnly = thinning === 0;
    const fallbackSvgPath = getSvgPathFromStroke(renderedStroke, !isDerivedStrokeOnly);

    return (
        <DrawingPathSvg
            className={drawingPathClassName}
            color={color}
            getStrokeColor={getStrokeColor}
            isStrokeOnly={isDerivedStrokeOnly}
            size={size}
            svgPath={fallbackSvgPath}
            {...rest}
        />
    );
};

export default React.memo(DrawingPath);
